import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { SonataMORE } from '@/components/common/SonataMORE';
import { useGlobalStore } from '@/store/global/index.ts';
import { getUser } from '@/store/global/user/selectors.ts';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { IoSettingsStore, useStore as useIOSettingsStore } from '@/store/local/ioSettings';
import { Flight } from './Flight/Flight.tsx';
import { FlightSettingsStyled } from './FlightSettings.styles';
import { FlightSettingsFooter } from './FlightSettingsFooter';
import { FlightSettingsHeader } from './FlightSettingsHeader';
import {
  generateInitialFlight,
  getFlightRanges,
  getFlightsTotals,
  getInitialState,
  getLineItemMarkers,
  toFlightsPayload
} from './flightSettings.helpers';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { LineItemSchedule } from '@/components/ioSettings/InsertionOrderForm/hooks/useIoLineItemSchedules';
import type { FlightState, FlightsState } from './flightSettings.helpers';

type Props = {
  baseCurrency: BaseCurrencyInterface;
  errors?: Record<string, any>;
  ioIsPublished?: boolean;
  items: InsertionOrderFlight[];
  /** Schedules of the IO's line items, shown in every flight calendar. */
  lineItems?: LineItemSchedule[];
  onChange: (flights: InsertionOrderFlight[]) => void;
};

const NO_LINE_ITEMS: LineItemSchedule[] = [];

export function FlightSettings({
  baseCurrency,
  errors,
  ioIsPublished = false,
  items = [],
  lineItems = NO_LINE_ITEMS,
  onChange
}: Props) {
  const ioSettingsState = useIOSettingsStore() as IoSettingsStore;
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const [state, setState] = useState<FlightsState>(() => getInitialState(items));
  const { formatMessage } = useIntl();

  const { optimizationModel, setOptimizationModel, countries, type: mediaChannelSelected } = ioSettingsState;
  const userJSON = user.toJSON();

  const isDooh = mediaChannelSelected && isDOOHCampaign(mediaChannelSelected);

  const flights = Array.from(state.values());

  const commit = (newState: FlightsState) => {
    setState(newState);
    onChange(toFlightsPayload(newState));
  };

  const handleFlightChange = (flightId: string, patch: Partial<FlightState>) => {
    const flight = state.get(flightId);
    if (flight) {
      commit(new Map(state).set(flightId, { ...flight, ...patch }));
    }
  };

  const handleDeleteFlight = (flightId: string) => {
    const newState = new Map(state);
    newState.delete(flightId);
    commit(newState);
  };

  const handleAddFlightClick = () => {
    const newFlight = generateInitialFlight(state.size);
    setState(new Map(state).set(newFlight._id, newFlight));
  };

  const flightRanges = useMemo(
    () =>
      getFlightRanges(Array.from(state.values()), index =>
        formatMessage(
          { defaultMessage: 'Flight {index}', id: 'spc.flightSettings.flight.defaultLabel' },
          { index: index + 1 }
        )
      ),
    [state, formatMessage]
  );
  const markers = useMemo(() => getLineItemMarkers(lineItems), [lineItems]);

  const { dates: flightsDates, totalBudget } = getFlightsTotals(flights);

  return (
    <FlightSettingsStyled className="flightSettings">
      <FlightSettingsHeader dates={flightsDates} flightsCount={flights.length} />
      <main className="flightSettings__main">
        {errors?.message && (
          <div className="flightSettings__mainError">
            <i className="fa-regular fa-circle-x fa-xs" />
            {errors.message}
          </div>
        )}
        <div className="flightSettings__list">
          {flights.map((item, i) => (
            <Flight
              baseCurrency={baseCurrency}
              errors={errors?.[i]}
              flightRanges={flightRanges}
              index={i}
              ioIsPublished={ioIsPublished}
              item={item}
              key={item._id}
              markers={markers}
              onChange={handleFlightChange}
              onDelete={handleDeleteFlight}
            />
          ))}
        </div>
        <FlightSettingsFooter
          dates={flightsDates}
          onAddFlight={handleAddFlightClick}
          total={normalizeWithUnit(totalBudget, baseCurrency, 2)}
        />
      </main>
      {!isDooh && (
        <SonataMORE
          campaignCountries={countries}
          campaignOptimizationModel={optimizationModel}
          currencyInfo={baseCurrency}
          goals={flights.map(f => ({
            target: f.goal?.target,
            timeFrame: 'overall',
            type: 'budget'
          }))}
          setCampaignOptimizationModel={setOptimizationModel}
          user={userJSON}
          userRoles={userRoles}
        />
      )}
    </FlightSettingsStyled>
  );
}
